import { IWasmCustomData, TRoutePopupInfoColorVariant } from "~/interfaces";
import { TEXTURES_NAMES } from "./const";
import { TUrlProviderNamesVariants } from "./interfaces";
import { getEmptyImageData, isUrlIcon } from "./utils";
import Wasm from "./wasm";
import { createRoutePopupClosure, createRoutePopupContentLines, createRoutePopupPubtSection, createRoutePopupPubtSummary, getCenteredIcon, getCircle, getCircleImage, getGetRouteStartEnd, getLocationShareDeviceNumber, getLocationShareImage, getPathCaption, getReview, getRoutePointCircle, getSmallNumber, getText, getWeatherIconTexture, svgToCanvas, windSvgToCanvas, getWasmTexture } from "./textures-graphics";
import { TPositionShareImageVariant } from "./store/interfaces";

export function bindWasmTextureUrlProvider(wasm: Wasm) {
	const mapContent = wasm.mapContent;
	const emptyImageData = getEmptyImageData();

	mapContent.setRequestDataCallback(async requestedUrl => {
		let wasHandled = false;
		const correctUrl = requestedUrl.match(/^\/\//u)
			? `https:${requestedUrl}`
			: requestedUrl.match(/^\//u)
				? `${location.origin}${requestedUrl}`
				: requestedUrl;
		const url = new URL(correctUrl);

		switch (url.protocol) {
			case `${TEXTURES_NAMES.main}:`: {
				const textureName = url.host;
				// @ts-ignore
				const textureData = getWasmTexture(wasm.textures, textureName, wasm.textures.scale);

				if (textureData) {
					wasHandled = true;
					mapContent.setRequestedData(requestedUrl, textureData.imageData);
				}
				break;
			}

			case `${TEXTURES_NAMES.custom}:`: {
				const host = url.host as TUrlProviderNamesVariants;

				switch (host) {
					case "user-marks": {
						const order = parseFloat(url.searchParams.get("order"));
						const isActive = url.searchParams.get("active") === "1";
						const opacity = parseFloat(url.searchParams.get("opacity"));
						const scale = parseFloat(url.searchParams.get("scale"));
						const textureData = getSmallNumber({
							wasmTextures: wasm.textures,
							scale,
							value: order,
							isActive,
							opacity,
						});

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "marker-text": {
						const text = url.searchParams.get("text");
						const scale = parseFloat(url.searchParams.get("scale"));
						const textureData = getText(text, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "marker-review": {
						const reviewValue = parseFloat(url.searchParams.get("value"));
						const reviewActive = url.searchParams.get("active") === "1";
						const scale = parseFloat(url.searchParams.get("scale"));
						const textureData = getReview(reviewValue, reviewActive, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "circle-radius": {
						const width = parseFloat(url.searchParams.get("width"));
						const color = url.searchParams.get("color");
						const textureData = getCircle(color, width);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "route": {
						const isActive = url.searchParams.get("active") === "1";
						const isEnd = url.searchParams.get("start") === "0";
						const opacity = parseFloat(url.searchParams.get("opacity"));
						const scale = parseFloat(url.searchParams.get("scale"));
						const textWidth = parseFloat(url.searchParams.get("textWidth"));
						const routeStartEndData = getGetRouteStartEnd({
							wasmTextures: wasm.textures,
							isActive,
							isEnd,
							opacity,
							scale,
							textWidth,
						});

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, routeStartEndData.imageData);
						break;
					}

					case "position-share-device": {
						const order = parseFloat(url.searchParams.get("order"));
						const isActive = url.searchParams.get("active") === "1";
						const scale = parseFloat(url.searchParams.get("scale"));
						const textureData = getLocationShareDeviceNumber(wasm.textures, order, isActive, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					// poloha - obal kolecko + fotka (neaktivni)
					case "position-share-image": {
						const img = url.searchParams.get("img");
						const variant = url.searchParams.get("variant") as TPositionShareImageVariant;
						const scale = parseFloat(url.searchParams.get("scale"));
						const textureData = await getLocationShareImage(wasm.textures, img, variant, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "path-caption": {
						const text = url.searchParams.get("text");
						const scale = parseFloat(url.searchParams.get("scale"));
						const textureData = getPathCaption(text, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "route-point-circle": {
						const radius = parseFloat(url.searchParams.get("radius"));
						const color = url.searchParams.get("color");
						const scale = parseFloat(url.searchParams.get("scale"));
						const textureData = getRoutePointCircle(radius, scale, color);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, textureData.imageData);
						break;
					}

					case "circle-image": {
						const size = parseFloat(url.searchParams.get("size"));
						const img = url.searchParams.get("img");
						const iconSize = parseFloat(url.searchParams.get("iconsize"));
						const scale = parseFloat(url.searchParams.get("scale"));
						const isIcon = isUrlIcon(img);
						let imageData: ImageData = null;

						if (isIcon) {
							const iconData = await getCenteredIcon(img, size, iconSize, scale);

							imageData = iconData.imageData;
						} else {
							const circleData = await getCircleImage(img, size, false);

							imageData = circleData.imageData;
						}

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, imageData);
						break;
					}

					case "route-popup-info": {
						const jsonStr = url.searchParams.get("json");
						const lines = JSON.parse(jsonStr);
						const variant = url.searchParams.get("variant");
						const scale = parseFloat(url.searchParams.get("scale"));
						const linesData = createRoutePopupContentLines(lines, variant as TRoutePopupInfoColorVariant, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-weather": {
						const icon = url.searchParams.get("icon");
						const value = url.searchParams.get("value");
						const time = url.searchParams.get("time");
						const forceFill = url.searchParams.get("forcefill");
						const windAngleParam = url.searchParams.get("windangle");
						const textBold = url.searchParams.get("bold") === "1";
						const scale = parseFloat(url.searchParams.get("scale"));
						let iconElem: HTMLCanvasElement = null;

						if (icon) {
							try {
								iconElem = await svgToCanvas(icon, scale, forceFill);
							} catch (exc) {
								/* eslint-disable-next-line */
								console.error(`Wasm - route-popup-weather provider exception ${exc.message}`);
							}
						} else if (windAngleParam) {
							const windAngle = parseFloat(windAngleParam);

							iconElem = await windSvgToCanvas(windAngle, scale);
						}

						const linesData = getWeatherIconTexture(value, time, textBold, scale, iconElem);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-pubt-summary": {
						const text = url.searchParams.get("text");
						const warning = url.searchParams.get("warning") === "1";
						const icons = JSON.parse(url.searchParams.get("icons")) as IWasmCustomData["routePopupPubtSummary"]["icons"];
						const scale = parseFloat(url.searchParams.get("scale"));
						const linesData = createRoutePopupPubtSummary(wasm.textures, {
							text,
							warning,
							icons,
						}, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-pubt-section": {
						const title = url.searchParams.get("title");
						const subTitle = url.searchParams.get("subtitle");
						const desc = url.searchParams.get("desc");
						const titleColor = url.searchParams.get("titlecolor");
						const descColor = url.searchParams.get("desccolor");
						const scale = parseFloat(url.searchParams.get("scale"));
						const linesData = createRoutePopupPubtSection(wasm.textures, {
							title,
							subTitle,
							desc,
							titleColor,
							descColor,
						}, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					case "route-popup-closure": {
						const icon = url.searchParams.get("icon") as IWasmCustomData["routePopupClosure"]["icon"];
						const text = url.searchParams.get("text");
						const scale = parseFloat(url.searchParams.get("scale"));
						const linesData = createRoutePopupClosure(wasm.textures, {
							icon,
							text,
						}, scale);

						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, linesData.imageData);
						break;
					}

					default:
				}
				break;
			}

			default: {
				// fetch data
				try {
					const response = await fetch(correctUrl);

					if (response.ok) {
						const data = await response.arrayBuffer();

						// set data back to conent
						wasHandled = true;
						mapContent.setRequestedData(requestedUrl, new Uint8Array(data));
					}
				} catch (exc) {
					/* eslint-disable-next-line */
					console.error(`Wasm url provider exception - ${exc.message} for url ${requestedUrl}`);
				}
			}
		}

		if (!wasHandled) {
			/* eslint-disable-next-line */
			console.log(`Request url for ${requestedUrl} [unhandled]`);
			/** prazdny obrazek */
			mapContent.setRequestedData(requestedUrl, emptyImageData);
		}
	});
}
